/**
 * navigation.js — single source of truth for app navigation.
 *
 * The login response carries a dynamic `enabledServices` tree:
 *
 *   { [serviceName]: { [moduleName]: { [submoduleName]: Feature[] } } }
 *   Feature = { feature, display, code, api }
 *
 * The Sidebar renders this tree (via sessionDB), down to the submodule
 * level. Clicking a submodule always navigates to a route:
 *
 *   1. If the submodule is listed in ROUTE_LINKS it opens its dedicated,
 *      fully implemented page (attendance, leave, org tables, ...).
 *   2. Otherwise it falls back to the generic SubmoduleFeaturesPage at
 *      /services/:service/:module/:submodule, which lists the
 *      submodule's features from the session tree.
 *
 * To wire up a new module later, build its page, register a route in
 * AppRoutes.jsx, and add one line to ROUTE_LINKS below — the sidebar and
 * dashboard pick it up automatically.
 */
import { getEnabledServices } from '../utils/sessionDB';
import { ONBOARDING_ROUTE_BY_SUBMODULE } from "../modules/service-explorer/pages/onboardingRoutes";

/*
import { CustomerListPage, CustomerOnboardingPage, CustomerViewPage } from '../modules/crm/pages';
import {
  CRM_ONBOARDING_LIST_PATH,
  CRM_ONBOARDING_ADD_PATH,
  CRM_ONBOARDING_EDIT_PATH,
} from '../modules/crm/routes';
*/

const ONBOARDING_PRIMARY_ORDER = [
  "Employee Information",
  "Current Employment",
  "Employee Shifts Mapping",
  "Shift Mapping",
  "Previous Employment",
  "Education Records",
  "Employee Documents",
  "Account Details",
  "Emergency Info",
  "Assets & Access",
  "Position History"
];


function ensureCrmSalesModules(enabledServices = {}) {
  const nextServices = JSON.parse(JSON.stringify(enabledServices || {}));
  const crmModules = nextServices.CRM && typeof nextServices.CRM === "object"
    ? nextServices.CRM
    : {};

  const salesModule = crmModules.Sales && typeof crmModules.Sales === "object"
    ? crmModules.Sales
    : {};

  const normalizedSalesModule = {};

  if (Array.isArray(salesModule.Quotation)) {
    normalizedSalesModule.Quotation = salesModule.Quotation;
  } else if (Array.isArray(salesModule.Quotations)) {
    normalizedSalesModule.Quotation = salesModule.Quotations;
  }

  if (Array.isArray(salesModule["PMC Cost Heads"])) {
    normalizedSalesModule["PMC Cost Heads"] = salesModule["PMC Cost Heads"];
  }

  if (Array.isArray(salesModule["Approval Rules"])) {
    normalizedSalesModule["Approval Rules"] = salesModule["Approval Rules"];
  }

  if (Array.isArray(salesModule["Net Price Configuration"])) {
    normalizedSalesModule["Net Price Configuration"] = salesModule["Net Price Configuration"];
  }

  if (Array.isArray(salesModule["Terms Library"])) {
    normalizedSalesModule["Terms Library"] = salesModule["Terms Library"];
  }

  if (Object.keys(normalizedSalesModule).length > 0) {
    crmModules.Sales = normalizedSalesModule;
  } else {
    delete crmModules.Sales;
  }

  nextServices.CRM = crmModules;
  return nextServices;
}


function normalizeOnboardingSubmodules(enabledServices = {}) {
  const nextServices = JSON.parse(JSON.stringify(enabledServices || {}));

  Object.values(nextServices).forEach((modules) => {
    if (!modules || typeof modules !== "object") {
      return;
    }

    const onboardingModules = modules.Onboarding;
    if (!onboardingModules || typeof onboardingModules !== "object") {
      return;
    }

    const mergedAssetsAccessFeatures = [
      ...(Array.isArray(onboardingModules["Assets & Access"]) ? onboardingModules["Assets & Access"] : []),
      ...(Array.isArray(onboardingModules["Asset Allocation"]) ? onboardingModules["Asset Allocation"] : []),
      ...(Array.isArray(onboardingModules["Access Card"]) ? onboardingModules["Access Card"] : [])
    ];

    if (mergedAssetsAccessFeatures.length > 0) {
      const featureIndex = new Map();
      mergedAssetsAccessFeatures.forEach((feature) => {
        if (!isDisplayEnabled(feature) || !feature?.code || !feature?.api) {
          return;
        }
        featureIndex.set(feature.code, feature);
      });

      onboardingModules["Assets & Access"] = Array.from(featureIndex.values());
    }

    delete onboardingModules["Asset Allocation"];
    delete onboardingModules["Access Card"];
    delete onboardingModules["Onboarding Progress"];
  });

  return nextServices;
}

function isDisplayEnabled(feature) {
  return feature?.display === true;
}

function getDisplayableFeatures(features) {
  return Array.isArray(features) ? features.filter(isDisplayEnabled) : [];
}

function filterDisplayableFeatureTree(enabledServices = {}) {
  const nextServices = {};

  Object.entries(enabledServices || {}).forEach(([service, modules]) => {
    const nextModules = {};

    Object.entries(modules || {}).forEach(([module, submodules]) => {
      const nextSubmodules = {};

      Object.entries(submodules || {}).forEach(([submodule, features]) => {
        const displayableFeatures = getDisplayableFeatures(features);
        if (displayableFeatures.length > 0) {
          nextSubmodules[submodule] = displayableFeatures;
        }
      });

      if (Object.keys(nextSubmodules).length > 0) {
        nextModules[module] = nextSubmodules;
      }
    });

    if (Object.keys(nextModules).length > 0) {
      nextServices[service] = nextModules;
    }
  });

  return nextServices;
}

function getOrderedSubmoduleNames(service, module, submodules = {}) {
  const names = Object.keys(submodules || {});
  const isOnboardingModule = cleanName(module).toLowerCase() === "onboarding";

  if (!isOnboardingModule) {
    return names;
  }

  const prioritized = ONBOARDING_PRIMARY_ORDER.filter((name) => names.includes(name));
  const remaining = names.filter((name) => !ONBOARDING_PRIMARY_ORDER.includes(name));
  return [...prioritized, ...remaining];
}


/**
 * Known submodule -> dedicated page routes.
 * Keys are `${service}::${module}::${submodule}` exactly as they appear
 * in enabledServices.
 */
export const ROUTE_LINKS = {
  'HRMS ESS::Leave Management::Request for Leave': '/hrms-ess/apply-leave',
  'HRMS ESS::Attendence Management::Reports': '/hrms-ess/attendance',
  'HRMS ESS::Attendence Management::Mark Attendance': '/hrms-ess/attendance/mark',
  'HRMS ESS::Services::Holiday Calendar': '/hrms-ess/holidays',
  'HRMS ESS::Leave Management::Leave Report': '/hrms-ess/leave-report',
  'HRMS ESS::Services::Announcements': '/hrms-ess/announcements',
  'HRMS ESS::Services::Org Chart': '/hrms-ess/org-chart',
  'HRMS ESS::Services::My Profile': '/hrms-ess/employee-info',
  'HRMS Team::CoreHR Management::Announcements Configuration': '/hrms-team/announcements',
  //'HRMS ESS::Services::My Announcements': '/hrms-ess/announcements',
  'HRMS Team::Attendence Management::Reports': '/hrms-team/attendance',
  //'HRMS Team::Attendence Management::Approve': '/hrms-team/leave-requests',
  'HRMS Team::Leave Management::Leave Report': '/hrms-team/leave-requests',
  'HRMS Team::Employees::Profile': '/hrms-team/employee-info',
  'HRMS Team::CoreHR Management::Org Tables Excel Load': '/hrms-team/config/org-tables',
  'HRMS Team::CoreHR Management::Calendar Events Configuration': '/hrms-team/config/holidays',
  'HRMS Team::CoreHR Management::Role Services Configuration': '/hrms-team/config/roles-services',
  'HRMS Team::CoreHR Management::Roles & Permissions': '/hrms-team/config/roles-services/create',
  //'HRMS Team::Onboarding::Employee Information': '/hrms-team/onboarding/employee-information',
  'HRMS Team::Onboarding::Employee Information': ONBOARDING_ROUTE_BY_SUBMODULE["Employee Information"],
  'HRMS Team::Onboarding::Current Employment': ONBOARDING_ROUTE_BY_SUBMODULE["Current Employment"],
  'HRMS Team::Onboarding::Employee Shifts Mapping': ONBOARDING_ROUTE_BY_SUBMODULE["Shift Mapping"],
  'HRMS Team::Onboarding::Shift Mapping': ONBOARDING_ROUTE_BY_SUBMODULE["Shift Mapping"],
  'HRMS Team::Onboarding::Previous Employment': ONBOARDING_ROUTE_BY_SUBMODULE["Previous Employment"],
  'HRMS Team::Onboarding::Education Records': ONBOARDING_ROUTE_BY_SUBMODULE["Education Records"],
  'HRMS Team::Onboarding::Employee Documents': ONBOARDING_ROUTE_BY_SUBMODULE["Employee Documents"],
  'HRMS Team::Onboarding::Account Details': ONBOARDING_ROUTE_BY_SUBMODULE["Account Details"],
  'HRMS Team::Onboarding::Emergency Info': ONBOARDING_ROUTE_BY_SUBMODULE["Emergency Info"],
  'HRMS Team::Onboarding::Assets & Access': ONBOARDING_ROUTE_BY_SUBMODULE["Assets & Access"],
  'HRMS Team::Onboarding::Position History': ONBOARDING_ROUTE_BY_SUBMODULE["Position History"],
  'HRMS Team::Employees::Onboarding': '/hrms-team/onboarding/employees',
  'HRMS Team::CoreHR Management::Shifts Master Configuration': '/hrms-team/config/shifts',
  'HRMS Team::CoreHR Management::Polls Configuration': '/hrms-team/polls',
  //'HRMS Team::CoreHR Management::Polls': '/hrms-team/polls',
  //'HRMS Team::CoreHR Management::Poll Configuration': '/hrms-team/polls',
  'HRMS ESS::Services::Polls': '/hrms-ess/polls',
  'PSM::Tenant Management::Onboarding': '/psm/config/tenants',
  'HRMS Team::CoreHR Management::Week Work Schedule': '/hrms-team/config/weekly-work-schedule',
  'FSM::Ticket Management::Customer Complaints': '/fsm/customer-complaints',
  'FSM::Ticket Management::Create Customer Complaint': '/fsm/create-customer-complaint',
  'FSM::Services Management::Complaints Checklist Configuration': '/fsm/complaints-checklist',
  'FSM::Services Management::Complaints Checklist': '/fsm/complaints-checklist',
  'CRM::Customer::Onboarding': '/services/CRM/Customer/Onboarding',
  'CRM::Customer::Inventory': '/services/CRM/Customer/Customer-Assets',
  'CRM::Customer::Inventory': '/services/CRM/Customer/Customer-Assets',

  'CRM::Quotation::Approval Rules': '/sales/configurations/approval-rules',
  'CRM::Quotation::Net Price Rules': '/sales/configurations/net-price-configuration',
  'CRM::Quotation::PMC Cost Heads': '/sales/configurations/pmc-cost-heads',
  'CRM::Quotation::Terms Library': '/sales/configurations/terms-library',
  'CRM::Quotation::Quotations': '/sales/quotations/quotation',
  'CRM::Quotation::PDF Download Rules': '/sales/configurations/pdf-download-rules',
  
  'IMS::Inventory Management::Record Inventory Out': '/inventory-management/stock-transfer',
  'IMS::Inventory Management::Record Inventory In': '/inventory-management/record-inventory-in',
  'IMS::Inventory Management::Stock Position': '/inventory-management/stock-position',
  'IMS::Inventory Management::Stock Journal': '/inventory-management/stock-journal',
  'IMS::Inventory Management::Customer Allocation': '/inventory-management/customer-allocations',
  'IMS::Inventory Management::Reorder Alerts': '/inventory-management/reorder-alerts',
  'IMS::Inventory Management::Delivery Challan': '/inventory-management/delivery-challan',
  'IMS::Inventory Management::Revoke & Unlock Requests': '/inventory-management/revoke-unlock-requests',
  
  'IMS::Master Data::Units of Measure': '/product-metrics',
  'IMS::Master Data::Product Catalogue': '/IMS/inventory/products',
  'IMS::Master Data::Warehouse Catalogue': '/IMS/inventory/locations',
  'IMS::Master Data::Vendor Catalogue': '/IMS/inventory/vendors',

  'HRMS Team::Conveyance::Local Policy': '/hrms-team/config/expense-policy',
  'HRMS ESS::Claims Management::Local Claims': '/hrms-ess/conveyance-claims',
  'HRMS Team::Conveyance::Local Claims Report': '/hrms-team/conveyance-claims',

  'HRMS Team::CoreHR Management::Expense Policy': '/hrms-team/config/expense-policy',

  'PSM::Tickets Management::Tickets': '/psm/tickets',
  'PSM::Tenant Management::Requests': '/psm/tenant-requests',
  
  'HRMS Team::CoreHR Management::Org Settings': '/hrms-team/config/org-settings',

  'HRMS Team::Employees::Requests': '/hrms-team/employees/requests',

  'CRM::Lead Management::Leads': '/crm/lead-management/leads',

  'FSM::Services Management::Services & Complaint Types': '/fsm/products-services',
  'PSM::Tenant Management::Announcements': '/psm/tenant-announcements',

  'HRMS Team::Dashboards::View': '/dashboards/hr',
  'HRMS ESS::Dashboards::View': '/dashboards/ess',
  'CRM::Dashboards::View': '/dashboards/crm',
  'FSM::Dashboards::View': '/dashboards/fsm',
  'IMS::Dashboards::View': '/dashboards/ims',
  'PSM::Dashboards::View': '/dashboards/pms',

  'HRMS Team::Dashboards::Configure': '/hrms-team/config/dashboards',
  'HRMS Team::Conveyance::Outstation Policy': '/hrms-team/config/travel-policy',
  'HRMS ESS::Claims Management::Outstation Claims':'/outstation-travel',
  'HRMS Team::Conveyance::Outstation Claims Report': '/outstation-travel/report',
};

/** Route a submodule click resolves to (dedicated page or generic fallback). */
export function getSubmoduleRoute(service, module, submodule) {
  return (
    ROUTE_LINKS[`${service}::${module}::${submodule}`] ||
      `/services/${encodeURIComponent(service)}/${encodeURIComponent(module)}/${encodeURIComponent(submodule)}`
  );
}

/** Feature list for one submodule, straight from the session tree. */
export function getSubmoduleFeatures(service, module, submodule) {
  const features = ((getEnabledServices()[service] || {})[module] || {})[submodule];
  return getDisplayableFeatures(features);
}

/**
 * enabledServices -> ordered nav tree, populated down to submodules only:
 *   [{ name, modules: [{ name, submodules: [{ name, to }] }] }]
 *
export function buildNavTree(enabledServices = getEnabledServices()) {
  return Object.entries(enabledServices || {}).map(([service, modules]) => ({
    name: service,
    modules: Object.entries(modules || {}).map(([module, submodules]) => ({
      name: module,
      submodules: Object.keys(submodules || {}).map((submodule) => ({
        name: submodule,
        to: getSubmoduleRoute(service, module, submodule),
      })),
    })),
  }));
}
  */
 export function buildNavTree(enabledServices = getEnabledServices()) {
  const normalizedServices = filterDisplayableFeatureTree(
    ensureCrmSalesModules(normalizeOnboardingSubmodules(enabledServices)));

  return Object.entries(normalizedServices || {}).map(([service, modules]) => ({
    name: service,
    modules: Object.entries(modules || {}).map(([module, submodules]) => ({
      name: module,
      submodules: getOrderedSubmoduleNames(service, module, submodules).map((submodule) => ({
        name: submodule,
        to: getSubmoduleRoute(service, module, submodule),
      })),
    })),
  }));
}

/** Display name cleanup shared by the sidebar and drilldown pages. */
export function cleanName(raw) {
  return String(raw || '')
    .replace(/\s*Configuration\s*$/i, '')
    .replace(/_/g, ' ')
    .trim();
}

/**
 * Active-route check. Root is the leave portal index and must match exactly,
 * everything else matches by prefix so child routes keep their parent
 * submodule highlighted.
 */
export function isPathActive(pathname, to) {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * Static route -> breadcrumb label map used by AppLayout to render a
 * consistent breadcrumb trail on every page. Purely presentational — it
 * has no bearing on how enabledServices is loaded or rendered.
 */
const BREADCRUMB_ROUTES = {
  '/dashboards/hr':                          { label: 'HR Dashboard',      parent: '/' },
  '/dashboards/ess':                         { label: 'ESS Dashboard',     parent: '/' },
  '/dashboards/crm':                         { label: 'CRM Dashboard',     parent: '/' },
  '/dashboards/fsm':                         { label: 'FSM Dashboard',     parent: '/' },
  '/dashboards/ims':                         { label: 'IMS Dashboard',     parent: '/' },
  '/dashboards/pms':                         { label: 'PMS Dashboard',     parent: '/' },
  '/hrms-ess/apply-leave':                    { label: 'Apply Leave',       parent: '/' },
  '/hrms-ess/leave-history':                  { label: 'Leave History',     parent: '/' },
  '/hrms-team/leave-history':                 { label: 'Leave History',     parent: '/' },
  '/hrms-team/manager':                       { label: 'Manager Dashboard', parent: '/' },
  '/hrms-team/leave-requests':                { label: 'Leave Approvals',   parent: '/hrms-team/manager' },
  '/hrms-ess/attendance':                     { label: 'Attendance',        parent: '/' },
  '/hrms-ess/attendance/mark':                { label: 'Mark Attendance',   parent: '/hrms-ess/attendance' },
  '/hrms-ess/attendance/regularization':      { label: 'Regularization',    parent: '/hrms-ess/attendance' },
  '/hrms-ess/holidays':                       { label: 'Holidays',          parent: '/' },
  '/hrms-team/config/org-tables':             { label: 'Org Tables',        parent: '/' },
  '/hrms-team/config/roles-services':         { label: 'Roles & Permissions', parent: '/' },
  '/hrms-team/config/roles-services/create':  { label: 'Create Role',       parent: '/hrms-team/config/roles-services' },
  'roles-services/edit/:id':                  { label: 'Edit Role',         parent: '/hrms-team/config/roles-services' },
  '/hrms-team/onboarding/employee-information':{ label: 'Employee Information', parent: '/' },
  '/hrms-ess/announcements':                  { label: 'Announcements',     parent: '/' },
  '/hrms-team/announcements':                 { label: 'Announcements',     parent: '/' },
  '/hrms-team/announcements/review':          { label: 'Review Requests',   parent: '/hrms-team/announcements' },
  '/hrms-team/attendance':                    { label: 'Team Attendance',   parent: '/' },
  '/hrms-team/config/holidays':               { label: 'Admin Holidays',    parent: '/' },
  '/hrms-ess/org-chart':                      { label: 'Org Chart',         parent: '/' },
  '/hrms-ess/org-chart/unassigned':           { label: 'Unassigned',        parent: '/hrms-ess/org-chart' },
  '/hrms-ess/employee-info':                  { label: 'My Info',           parent: '/' },
  '/hrms-team/employee-info':                 { label: 'Employee Info',     parent: '/' },
  '/hrms-team/config/shifts':                 { label: 'Shifts Master',     parent: '/' },
  '/hrms-ess/polls':                          { label: 'Polls',             parent: '/' },
  '/hrms-team/polls':                         { label: 'My Polls',          parent: '/' },
  '/hrms-team/polls/review':                  { label: 'Review Queue',      parent: '/hrms-team/polls' },
  '/hrms-team/polls/log':                     { label: 'HR Poll Log',       parent: '/hrms-team/polls' },
  '/psm/config/tenants':                      { label: 'Tenant Management', parent: '/' },
  '/psm/config/tenants/create':               { label: 'Onboarding',        parent: '/psm/config/tenants' },
  '/psm/config/tenants/edit/:id':             { label: 'Onboarding',        parent: '/psm/config/tenants' },
  '/psm/config/tenants/access/:id':           { label: 'Services',          parent: '/psm/config/tenants' },
  '/crm/customers':                           { label: 'Customer List',     parent: '/' },
  '/crm/customer-information':                { label: 'Customer',          parent: '/services/CRM/Onboard Customer/Customer Assets' },
  '/sales/configurations/pmc-cost-heads':                   { label: 'PMC Cost Heads',        parent: '/' },
  '/sales/configurations/approval-rules':                   { label: 'Approval Rules',        parent: '/' },
  '/sales/configurations/terms-library':                    { label: 'Terms Library',         parent: '/' },
  '/sales/quotations/quotation':                            { label: 'Quotation',             parent: '/' },
  '/sales/quotations/quotation/create': { label: 'Create Quotation', parent: '/sales/quotations/quotation' },
  '/sales/configurations/net-price-configuration': { label: 'Net Price Configuration', parent: '/' },
  '/hrms-team/config/weekly-work-schedule':   { label: 'Weekly Work Schedule',  parent: '/' },
  '/fsm/customer-complaints':                 { label: 'Customer Complaints',   parent: '/' },
  '/fsm/create-customer-complaint':           { label: 'Create Complaint',      parent: '/fsm/customer-complaints' },
  '/fsm/complaints-checklist':                { label: 'Complaints Checklist',  parent: '/' },
  '/fsm/complaints-checklist/add':            { label: 'Create Template',       parent: '/fsm/complaints-checklist' },
  '/fsm/complaints-checklist/update/:id':                   { label: 'Edit Template',         parent: '/fsm/complaints-checklist' },
  '/inventory-management/stock-position':                  { label: 'Inventory Management',  parent: '/' },
  '/inventory-management/record-inventory-in':              { label: 'Record Inventory In',   parent: '/inventory-management/stock-position' },
  '/inventory-management/allocate-to-customer':             { label: 'Allocate to Customer',  parent: '/inventory-management/stock-position' },
  '/inventory-management/stock-transfer':                   { label: 'Stock Transfer',        parent: '/inventory-management/stock-position' },
  '/inventory-management/reorder-alerts':                   { label: 'Reorder Alerts',        parent: '/inventory-management/stock-position' },
  '/inventory-management/stock-position/:productId':        { label: 'Stock Position Detail', parent: '/inventory-management/stock-position' },
  '/inventory-management/allocation-detail/:allocationId':  { label: 'Allocation Detail',     parent: '/inventory-management/stock-position' },
  '/product-metrics':                                       { label: 'Masters Configuration', parent: '/' },
  '/IMS/inventory/products':                 { label: 'Product Catalogue', parent: '/' },
  '/IMS/inventory/products/new':             { label: 'Add Product',       parent: '/IMS/inventory/products' },
  '/IMS/inventory/products/:productId':      { label: 'Product Detail',    parent: '/IMS/inventory/products' },
  '/IMS/inventory/products/:productId/edit': { label: 'Edit Product',      parent: '/IMS/inventory/products' },
  '/IMS/inventory/vendors':                  { label: 'Product Vendor',    parent: '/' },
  '/IMS/inventory/vendors/new':              { label: 'Add Vendor',        parent: '/IMS/inventory/vendors' },
  '/IMS/inventory/vendors/:vendorId':        { label: 'Vendor Detail',     parent: '/IMS/inventory/vendors' },
  '/IMS/inventory/vendors/:vendorId/edit':   { label: 'Edit Vendor',       parent: '/IMS/inventory/vendors' },
  '/IMS/inventory/locations':                { label: 'Inventory Location', parent: '/' },
  '/IMS/inventory/locations/new':            { label: 'Add Warehouse',     parent: '/IMS/inventory/locations' },
  '/IMS/inventory/locations/:id':            { label: 'Warehouse Detail',  parent: '/IMS/inventory/locations' },
  '/IMS/inventory/locations/:id/edit':       { label: 'Edit Warehouse',    parent: '/IMS/inventory/locations' },
  '/IMS/inventory/locations/:id/configure':  { label: 'Configure Stock Levels', parent: '/IMS/inventory/locations' },
  '/services/CRM/Customer/Onboarding':                      { label: 'Onboarding',            parent: '/' },
  '/services/CRM/Customer/Onboarding/add':                  { label: 'Add Customer',          parent: '/services/CRM/Customer/Onboarding' },
  '/services/CRM/Customer/Onboarding/edit/:customerId':     { label: 'Edit Customer',         parent: '/services/CRM/Customer/Onboarding' },
 '/hrms-team/config/expense-policy':                        { label: 'LOCAL Policy',        parent: '/' },
  '/hrms-ess/conveyance-claims':                             { label: 'My Conveyance Claims',  parent: '/' },
  '/hrms-ess/conveyance-claims/rate-per-km/new':             { label: 'New Per-km Claim',      parent: '/hrms-ess/conveyance-claims' },
  '/hrms-ess/conveyance-claims/rate-per-km/:claimId':        { label: 'Per-km Claim',          parent: '/hrms-ess/conveyance-claims' },
  '/hrms-ess/conveyance-claims/fixed-monthly/new':           { label: 'New Monthly Claim',     parent: '/hrms-ess/conveyance-claims' },
  '/hrms-ess/conveyance-claims/fixed-monthly/:claimId':      { label: 'Monthly Claim',         parent: '/hrms-ess/conveyance-claims' },
  '/hrms-ess/conveyance-claims/maintenance/new':             { label: 'New Maintenance Claim', parent: '/hrms-ess/conveyance-claims' },
  '/hrms-ess/conveyance-claims/maintenance/:claimId':        { label: 'Maintenance Claim',     parent: '/hrms-ess/conveyance-claims' },
  '/hrms-team/conveyance-claims':                            { label: 'Team Conveyance Claims', parent: '/' },
  '/hrms-team/conveyance-claims/all':                        { label: 'All Conveyance Claims',  parent: '/' },
  '/hrms-team/conveyance-claims/:claimId':                   { label: 'Claim Detail',           parent: '/hrms-team/conveyance-claims' }, 
  '/psm/tickets':                             { label: 'Tickets',           parent: '/' },
  '/psm/tickets/create':                      { label: 'Create Ticket',     parent: '/psm/tickets' },
  '/psm/tickets/:requestId':                  { label: 'Ticket Detail',     parent: '/psm/tickets' },
  '/psm/tickets/:requestId/edit':             { label: 'Edit Ticket',       parent: '/psm/tickets' },
  '/psm/tenant-requests':                     { label: 'Requests',          parent: '/' },
  '/sales/configurations/pdf-download-rules': { label: 'PDF Download Rules',    parent: '/' },
};

function collectTrail(path) {
  const entry = BREADCRUMB_ROUTES[path];
  if (!entry) return [];
  const rest = entry.parent === '/' ? [] : collectTrail(entry.parent);
  return [...rest, { label: entry.label, to: path }];
}

/**
 * Builds a Home -> ... -> Current breadcrumb trail for any known route.
 * Handles the two dynamic route shapes (generic service explorer and
 * role edit) explicitly; everything else is looked up in BREADCRUMB_ROUTES.
 */
export function buildBreadcrumbTrail(pathname) {
  if (!pathname || pathname === '/') return [{ label: 'Home', to: '/' }];


  const crmOnboardingNested = pathname.match(
    /^\/services\/CRM\/Customer\/Onboarding\/(add|view(?:\/[^/]+)?|edit(?:\/[^/]+)?)$/
  );
  if (crmOnboardingNested) {
    const segment = crmOnboardingNested[1];
    const isEdit = segment.startsWith('edit');
    const isView = segment.startsWith('view');
    return [
      { label: 'Home', to: '/' },
      { label: 'CRM' },
      { label: 'Customer' },
      { label: 'Onboarding', to: '/services/CRM/Customer/Onboarding' },
      { label: isView ? 'View Customer' : isEdit ? 'Edit Customer' : 'Add Customer' },
    ];
  }

  const quotationDetailMatch = pathname.match(/^\/sales\/quotations\/quotation\/([^/]+)$/);
  if (quotationDetailMatch && quotationDetailMatch[1] !== 'create') {
    return [
      { label: 'Home', to: '/' },
      ...collectTrail('/sales/quotations/quotation'),
      { label: 'Quotation Details', to: pathname },
    ];
  }

  const svcMatch = pathname.match(/^\/services\/([^/]+)\/([^/]+)\/([^/]+)$/);
  if (svcMatch) {
    const [, service, module, submodule] = svcMatch;
    return [
      { label: 'Home', to: '/' },
      { label: decodeURIComponent(service) },
      { label: cleanName(decodeURIComponent(module)) },
      { label: cleanName(decodeURIComponent(submodule)) },
    ];
  }

  const roleEditMatch = pathname.match(/^\/hrms-team\/config\/roles\/edit\/[^/]+$/);
  const crmEditMatch = pathname.match(/^\/services\/CRM\/Customer\/Onboarding\/edit\/[^/]+$/);
  const inventoryDetailMatch = pathname.match(/^\/inventory-management\/stock-position\/[^/]+$/);
  const allocationDetailMatch = pathname.match(/^\/inventory-management\/allocation-detail\/[^/]+$/);
  
  const key = roleEditMatch
    ? '/hrms-team/config/roles/edit/:id'
    : crmEditMatch
      ? '/services/CRM/Customer/Onboarding/edit/:customerId'
      : inventoryDetailMatch
        ? '/inventory-management/stock-position/:productId'
        : allocationDetailMatch
          ? '/inventory-management/allocation-detail/:allocationId'
          : pathname;

  const trail = collectTrail(key);
  if (trail.length === 0) return [{ label: 'Home', to: '/' }];
  return [{ label: 'Home', to: '/' }, ...trail];
}


/**
 * Always-visible shortcuts shown on the WelcomePage home screen regardless
 * of which services a given tenant/user has enabled.
 */
export const QUICK_LINKS = [
  { label: 'Profile',              sub: 'My Profile',                   to: '/hrms-ess/employee-info',        icon: 'profile' },
  { label: 'Leave Management',     sub: 'Apply, track & view leave',    to: '/hrms-ess/apply-leave',          icon: 'leave' },
  { label: 'Mark Attendance',      sub: 'Mark, view & regularize',      to: '/hrms-ess/attendance/mark',      icon: 'attendance' },
  { label: 'Change Password',      sub: 'Update your account password', to: '/hrms-ess/change-password',      icon: 'password' },
  { label: 'Holidays',             sub: 'Company holiday calendar',     to: '/hrms-ess/holidays',             icon: 'holidays' },
  //{ label: 'Manager Dashboard',  sub: 'Team leave & attendance',      to: '/hrms-team/manager',             icon: 'manager' },
  //{ label: 'Leave Approvals',    sub: 'Review team leave requests',   to: '/hrms-team/leave-requests',      icon: 'approvals' },
  //{ label: 'Org Tables',         sub: 'Master data Excel upload',     to: '/hrms-team/config/org-tables',   icon: 'org' },
 // { label: 'Roles & Permissions',sub: 'Manage roles & access',        to: '/hrms-team/config/roles-services', icon: 'roles' },
];
